import { computeSalePrice, useSiteSpecial } from "@/lib/pricingHelpers";

/**
 * Inline price display that automatically renders a slashed-through original
 * + animated gold sale price when an artwork or the site-wide special is on.
 *
 * Props:
 *   price     — the variant's regular price (number)
 *   artwork   — the artwork doc (for `special.sale_price`/`active`)
 *   size      — "sm" | "md" | "lg" (controls font size)
 *   align     — "left" | "right" — controls layout of struck price relative to sale
 *   compact   — when true, drops the percentage pill (e.g. inside variant rows)
 */
export default function SalePrice({ price, artwork, size = "md", align = "left", compact = false }) {
  const siteSpecial = useSiteSpecial();
  const { original, effective, isOnSale, pctOff } = computeSalePrice(price, artwork, siteSpecial);

  const baseCls =
    size === "lg" ? "text-3xl"
    : size === "sm" ? "text-[15px]"
    : "text-xl";

  if (!isOnSale) {
    return (
      <span className={`font-ui-tight ${baseCls} text-[#F2EFE8]`} data-testid="price-display">
        ${effective.toFixed(2)}
      </span>
    );
  }

  return (
    <span
      className={`stc-sale-price inline-flex items-baseline gap-2 ${align === "right" ? "flex-row-reverse" : ""}`}
      data-testid="price-display-on-sale"
    >
      <span className="stc-sale-strike font-ui-tight text-[#6a6660] line-through decoration-[#d97a7a] decoration-[1.5px]">
        ${original.toFixed(2)}
      </span>
      <span className={`stc-sale-effective font-ui-tight font-bold ${baseCls} text-[#D4A547]`}>
        ${effective.toFixed(2)}
      </span>
      {!compact && pctOff > 0 && (
        <span className="stc-sale-pct font-ui text-[9px] tracking-[0.22em] text-black bg-[#D4A547] px-1.5 py-0.5">
          {pctOff}% OFF
        </span>
      )}
    </span>
  );
}
